import {
  AiGoalsReportInput,
  GenAiGoalsReportMutation,
  Goals
} from '@cpd-app/generated';
import { buildGoalsReportRecord } from '@career-path-decide-nx/util-functions';
import {
  AIGoalsReport,
  Narratives,
  OccupationForExportPdf,
  UserProfile
} from '../models';
import { CONFIG } from '@cpd-app/configuration';
import { ViaReportStatus } from '@cpd-app/shared-module/constants';

/**
 * What the Goals Report's "Insights from Via" is told about the learner.
 *
 * AI-5. The record half (skills, education, service, work, readiness) comes
 * from `buildGoalsReportRecord`, which the subgraph's survey-regeneration path
 * uses too, so both describe a learner identically. Before AI-5 this read
 * `occupation.skills`, which the query had stopped selecting on 10 Sep, so
 * every report was written from an empty skills list.
 */
export const genAiGoalsReportInputMapper = (
  userProfile: UserProfile,
  goals: Goals,
  occupation: OccupationForExportPdf
) => {
  const record = buildGoalsReportRecord({
    militaryBranch: userProfile?.militaryBranch,
    userRanks: userProfile?.userRanks,
    userMocs: userProfile?.userMocs,
    userWorkExperiences: userProfile?.userWorkExperiences,
    userEducations: userProfile?.userEducations,
    ksas: occupation?.ksas,
    skillsCompatibility: occupation?.skillsCompatibility,
    readinessEvaluation: occupation?.readinessEvaluation
  });

  const surveyData: AiGoalsReportInput = {
    userGoals: {
      careerGoal: goals?.occupationTitle,
      institutionGoal: goals?.schoolTitle,
      programGoal: goals?.schoolProgramTitle
    },
    careerGoalAlignment: {
      interestsAnalysis: CONFIG.OCCUPATION.MATCH_QUALITY.find(
        (x) => x.id === occupation?.interestsMatchQualityId
      )?.name,
      lifestyleAnalysis: CONFIG.OCCUPATION.MATCH_QUALITY.find(
        (x) => x.id === occupation?.lifestyleMatchQualityId
      )?.name,
      skillsAnalysis: CONFIG.OCCUPATION.MATCH_QUALITY.find(
        (x) => x.id === occupation?.skillsMatchQualityId
      )?.name,
      valuesAnalysis: CONFIG.OCCUPATION.MATCH_QUALITY.find(
        (x) => x.id === occupation?.valuesMatchQualityId
      )?.name
    },
    interestsCompatibility: {
      userInterests: userProfile?.userInterests?.map((item) => {
        return {
          level: item.level,
          name: item.interestName
        };
      }),
      goalInterests: occupation?.interestsCompatibility?.map((item) => {
        return {
          name: item.name,
          level: item.level
        };
      })
    },
    workValuesCompatibility: {
      userValues: userProfile?.userWorkValues?.map((item) => {
        return {
          name: item.workValueName,
          level: item.level
        };
      }),
      goalValues: occupation?.workValuesCompatibility?.map((item) => {
        return {
          level: item.level,
          name: item.name
        };
      })
    },
    skillsCompatibilityAndGoalSelection: {
      careerGoal: goals?.occupationTitle,
      academicGoals:
        goals?.schoolTitle && goals?.schoolProgramTitle
          ? [goals.schoolTitle, goals.schoolProgramTitle]
          : [],
      ...record
    }
  };

  return surveyData;
};

export const getAiGoalsReportResponseParser = (
  genAiGoalsReportResponse: GenAiGoalsReportMutation['genAiGoalsReport']
): AIGoalsReport => {
  return {
    __typename: 'AIGoalsReportInsight',
    status: genAiGoalsReportResponse.status as ViaReportStatus,
    narratives: genAiGoalsReportResponse.narratives as Narratives,
    isOutdated: genAiGoalsReportResponse.outdated,
    rateLimit: {
      limit: genAiGoalsReportResponse.rateLimit.limit,
      resetTime: genAiGoalsReportResponse.rateLimit.resetTime,
      isAllowed: genAiGoalsReportResponse.rateLimit.allowed,
      remaining: genAiGoalsReportResponse.rateLimit.remaining
    },
    relatedTopics: genAiGoalsReportResponse.relatedTopics,
    writtenFor: genAiGoalsReportResponse.writtenFor ?? null
  };
};
